import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Rocket, ArrowLeft, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BoostSuccess = () => {
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const token = localStorage.getItem('token');

  // Determine initial status based on params
  const initialStatus = (!sessionId || !token) ? 'error' : 'loading';
  const [status, setStatus] = useState(initialStatus);

  useEffect(() => {
    if (initialStatus === 'error') {
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentData(data);

          if (data.payment_status === 'paid' && data.processed) {
            setStatus('success');
          } else if (data.status === 'expired' || data.payment_status === 'unpaid') {
            setStatus('failed');
          } else if (pollCount < 10) {
            // Continue polling
            setTimeout(() => setPollCount(prev => prev + 1), 2000);
          } else {
            setStatus('pending');
          }
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        if (pollCount < 10) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, token, pollCount]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold mb-2">Processing Payment...</h2>
              <p className="text-gray-500">Please wait while we confirm your payment</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-green-600 mb-2">Boost Activated!</h2>
              <p className="text-gray-500 mb-4">
                Your ad has been boosted successfully. It will now appear higher in search results.
              </p>
              {paymentData && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="font-medium">{formatPrice(paymentData.amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Status</span>
                    <span className="text-green-600 font-medium">Active</span>
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <Link to="/dashboard" className="block">
                  <Button className="w-full">
                    <Rocket className="w-4 h-4 mr-2" />
                    View My Ads
                  </Button>
                </Link>
                <Link to="/boost" className="block">
                  <Button variant="outline" className="w-full">
                    Boost More Ads
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-600 mb-2">Payment Failed</h2>
              <p className="text-gray-500 mb-6">
                Your payment could not be processed. Please try again.
              </p>
              <div className="space-y-3">
                <Link to="/boost" className="block">
                  <Button className="w-full">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </Link>
                <Link to="/dashboard" className="block">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-xl font-bold text-yellow-600 mb-2">Payment Pending</h2>
              <p className="text-gray-500 mb-6">
                Your payment is still being processed. Please check back later.
              </p>
              <Link to="/dashboard" className="block">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-600 mb-2">Something Went Wrong</h2>
              <p className="text-gray-500 mb-6">
                We could not verify your payment. Please contact support if you were charged.
              </p>
              <Link to="/boost" className="block">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Boost
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BoostSuccess;
